import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  CardContent,
  Card,
} from '@mui/material';
import {
  GetApp,
  CalendarToday,
  CheckCircle,
  RadioButtonUnchecked,
  Campaign,
  FilterList,
  SelectAll,
  CalendarTodayOutlined,
} from '@mui/icons-material';
import { generateReactPDFWithPreview } from './CampaignPage.helpers';
import CustomAppBar from '../../../../components/shared/CustomAppBar';
import { useProperty } from '../../../../context/PropertyContext';
import { PropertyListing } from '../../../../types/Property';
import { Container, Stack } from '@mui/system';
import CouponForm from './CouponForm';
import { Discount } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useAuth } from '../../../../context/AuthContext';
import { useCompany } from '../../../../context/CompanyContext';


type DateFilter = string;

const CampaignPage: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [selectedProperties, setSelectedProperties] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { userPropertyPosts, fetchUserPropertyPosts } = useProperty();
  const { company } = useCompany();
  const {userToken} = useAuth();

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      await fetchUserPropertyPosts(userToken!.user.id, company?.id);
      setIsLoading(false);
    };
    fetchPosts();
  }, [company?.id, fetchUserPropertyPosts, userToken]);

  const filteredProperties = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const properties = userPropertyPosts;

    return properties.filter((property: PropertyListing) => {
      if (!property.createdDate) return false;

      const propertyDate = new Date(property.createdDate);
      const propertyDateOnly = new Date(
        propertyDate.getFullYear(),
        propertyDate.getMonth(),
        propertyDate.getDate()
      );

      switch (dateFilter) {
        case 'today':
          return propertyDateOnly.getTime() === today.getTime();

        case 'last3days':
          const threeDaysAgo = new Date(today);
          threeDaysAgo.setDate(today.getDate() - 3);
          return propertyDateOnly >= threeDaysAgo;

        case 'lastWeek':
          const oneWeekAgo = new Date(today);
          oneWeekAgo.setDate(today.getDate() - 7);
          return propertyDateOnly >= oneWeekAgo;

        case 'lastMonth':
          const oneMonthAgo = new Date(today);
          oneMonthAgo.setMonth(today.getMonth() - 1);
          return propertyDateOnly >= oneMonthAgo;

        case 'all':
        default:
          return true;
      }
    });
  }, [dateFilter, userPropertyPosts]);

  // Función para obtener el label del filtro de fecha
  const getDateFilterLabel = (filter: DateFilter): string => {
    switch (filter) {
      case 'today':
        return 'Hoy';
      case 'last3days':
        return 'Últimos 3 días';
      case 'lastWeek':
        return 'Última semana';
      case 'lastMonth':
        return 'Último mes';
      case 'all':
        return 'Todas las fechas';
      default:
        return 'Todas las fechas';
    }
  };

  // Manejar selección individual de propiedades
  const handlePropertyToggle = (propertyId: number) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(propertyId)) {
      newSelected.delete(propertyId);
    } else {
      newSelected.add(propertyId);
    }
    setSelectedProperties(newSelected);
    setSelectAll(newSelected.size === filteredProperties.length);
  };

  // Manejar selección de todas las propiedades
  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectedProperties(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filteredProperties.map(p => p.id_Property));
      setSelectedProperties(allIds);
      setSelectAll(true);
    }
  };

  const generatePDF = async () => {
    if (selectedProperties.size === 0) return;

    setIsGeneratingPDF(true);

    try {
      const selectedProps = filteredProperties.filter(p =>
        selectedProperties.has(p.id_Property)
      );

      await generateReactPDFWithPreview(selectedProps, getDateFilterLabel(dateFilter));
      handleClose();
    } catch (error) {
      console.error('Error generando PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Función para cerrar modal y resetear estado
  const handleClose = () => {
    setDateFilter('all');
    setSelectedProperties(new Set());
    setSelectAll(false);
    setIsGeneratingPDF(false);
  };

  const handleCouponSuccess = () => {
    Swal.fire({
      icon: 'success',
      title: '¡Cupones creados!',
      text: 'Los cupones se han creado exitosamente!',
      confirmButtonColor: '#23afa4ff'
    });
    setShowCouponForm(false);
    handleClose();
  };

  // Actualizar selectAll cuando cambian las propiedades filtradas o seleccionadas
  React.useEffect(() => {
    setSelectAll(
      filteredProperties.length > 0 &&
      selectedProperties.size === filteredProperties.length
    );
  }, [filteredProperties.length, selectedProperties.size]);

  return (
    <>
      <CustomAppBar />

      {/* Contenedor principal con padding y margen superior */}
      <Container maxWidth="lg" sx={{ mt: { xs: '10vh', sm: '12vh' }, mb: 2, px: { xs: 1, sm: 2 } }}>
        <Paper
          elevation={4}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}
        >
          {/* Header con título principal */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #23afa4ff 0%, #1e8e8b 100%)',
              color: 'white',
              p: { xs: 2, sm: 3 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Campaign sx={{ fontSize: { xs: 24, sm: 28 } }} />
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                  Campaña de Propiedades
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  Selecciona y genera reportes de tus propiedades
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Contenido principal */}
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

            {/* Sección de filtros */}
            <Card
              elevation={1}
              sx={{
                mb: 2,
                borderRadius: 1,
                border: '1px solid #e0e4e7',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <FilterList sx={{ color: '#23afa4ff', fontSize: 18 }} />
                  <Typography variant="subtitle2" fontWeight="600" color="#333" sx={{ fontSize: '0.85rem' }}>
                    Filtros de Búsqueda
                  </Typography>
                </Stack>

                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel sx={{ fontSize: '0.85rem' }}>Filtro por fecha</InputLabel>
                  <Select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                    label="Filtro por fecha"
                    sx={{
                      borderRadius: 1,
                      fontSize: '0.85rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e4e7',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#23afa4ff',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#23afa4ff',
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Todas las fechas</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="today">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Hoy</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="last3days">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Últimos 3 días</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="lastWeek">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Última semana</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="lastMonth">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Último mes</Typography>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Estadísticas */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 2 }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Chip
                icon={<CalendarToday sx={{ fontSize: '14px !important' }} />}
                label={`${filteredProperties.length} propiedades encontradas`}
                size="small"
                sx={{
                  backgroundColor: '#23afa4ff',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  height: 28,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />

              <Chip
                icon={selectedProperties.size > 0 ?
                  <CheckCircle sx={{ fontSize: '14px !important' }} /> :
                  <RadioButtonUnchecked sx={{ fontSize: '14px !important' }} />
                }
                label={`${selectedProperties.size} seleccionadas`}
                variant={selectedProperties.size > 0 ? "filled" : "outlined"}
                size="small"
                sx={{
                  backgroundColor: selectedProperties.size > 0 ? '#4caf50' : 'transparent',
                  color: selectedProperties.size > 0 ? 'white' : '#666',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  height: 28,
                  border: selectedProperties.size === 0 ? '1px solid #e0e4e7' : 'none',
                  '& .MuiChip-icon': {
                    color: selectedProperties.size > 0 ? 'white' : '#666'
                  }
                }}
              />
            </Stack>

            {/* Selector de todas las propiedades */}
            {filteredProperties.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 1,
                  border: '1px solid #e0e4e7',
                  backgroundColor: '#f8fafc'
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllToggle}
                        indeterminate={selectedProperties.size > 0 && !selectAll}
                        size="small"
                        sx={{
                          color: '#23afa4ff',
                          '&.Mui-checked': { color: '#23afa4ff' },
                          '&.MuiCheckbox-indeterminate': { color: '#ff9800' }
                        }}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SelectAll sx={{ color: '#666', fontSize: 16 }} />
                        <Typography variant="body2" fontWeight="600" color="#333">
                          Seleccionar todas las propiedades
                        </Typography>
                      </Stack>
                    }
                  />
                </CardContent>
              </Card>
            )}

            <Divider sx={{ mb: 2, borderColor: '#e0e4e7' }} />

            {/* Lista de propiedades */}
            {filteredProperties.length === 0 ? (
              isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                  <CircularProgress color="primary" />
                </Box>
              ) : (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 1,
                    fontSize: '0.85rem',
                    '& .MuiAlert-icon': { fontSize: '1.2rem' }
                  }}
                >
                  <Typography variant="body2">
                    No se encontraron propiedades para el filtro de fecha seleccionado.
                  </Typography>
                </Alert>
              )
            ) : (
              <Card elevation={1} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <List
                  sx={{
                    maxHeight: '350px',
                    overflow: 'auto',
                    p: 0,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: '#a1a1a1',
                      },
                    },
                  }}
                >
                  {filteredProperties.map((property, index) => (
                    <React.Fragment key={property.id_Property}>
                      <ListItem
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          backgroundColor: selectedProperties.has(property.id_Property)
                            ? 'rgba(35, 175, 164, 0.08)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: selectedProperties.has(property.id_Property)
                              ? 'rgba(35, 175, 164, 0.12)'
                              : 'rgba(0, 0, 0, 0.04)',
                          },
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                        }}
                        onClick={() => handlePropertyToggle(property.id_Property)}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedProperties.has(property.id_Property)}
                              onChange={() => handlePropertyToggle(property.id_Property)}
                              icon={<RadioButtonUnchecked />}
                              checkedIcon={<CheckCircle />}
                              size="small"
                              sx={{
                                color: '#23afa4ff',
                                '&.Mui-checked': { color: '#23afa4ff' }
                              }}
                            />
                          }
                          label=""
                          sx={{ mr: 1 }}
                        />

                        <ListItemAvatar>
                          <Avatar
                            src={property.photos?.[0]}
                            variant="rounded"
                            sx={{
                              width: { xs: 50, sm: 60 },
                              height: { xs: 50, sm: 60 },
                              mr: { xs: 1, sm: 1.5 },
                              boxShadow: 1,
                              border: '1px solid #e0e4e7'
                            }}
                          >
                            🏠
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              color="#333"
                              sx={{
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                lineHeight: 1.3,
                                mb: 0.5
                              }}
                            >
                              {property.title}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5}>
                              <Chip
                                label={property.categoryName || 'Categoría no especificada'}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontWeight: 500,
                                  width: 'fit-content',
                                  height: 20,
                                  fontSize: '0.7rem'
                                }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight="600"
                                color="#2e7d32"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                💰 {property.price
                                  ? `${property.currency || 'CRC'} ${property.price.toLocaleString()}`
                                  : 'Precio no especificado'}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.65rem' }}
                              >
                                📅 Creada: {property.createdDate
                                  ? new Date(property.createdDate).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })
                                  : 'Fecha no disponible'}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index < filteredProperties.length - 1 && (
                        <Divider variant="inset" component="li" sx={{ borderColor: '#f0f0f0' }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            )}
          </CardContent>

          {/* Footer con botones */}
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              backgroundColor: '#f8fafc',
              borderTop: '1px solid #e0e4e7'
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2 }}
              justifyContent="flex-end"
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                size="small"
                sx={{
                  color: '#d32f2f',
                  borderColor: '#d32f2f',
                  fontWeight: '500',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  minHeight: 32,
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    borderColor: '#d32f2f',
                  }
                }}
              >
                Cancelar
              </Button>

              {/* Nuevo botón de cupones */}
              <Button
                onClick={() => setShowCouponForm(true)}
                variant="outlined"
                size="small"
                disabled={selectedProperties.size === 0}
                startIcon={<Discount sx={{ fontSize: 16 }} />}
                sx={{
                  color: '#23afa4ff',
                  borderColor: '#23afa4ff',
                  fontWeight: '500',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  minHeight: 32,
                  '&:hover': {
                    backgroundColor: '#23afa4ff',
                    color: 'white',
                    borderColor: '#23afa4ff',
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#ccc',
                  },
                }}
              >
                Crear Cupones ({selectedProperties.size})
              </Button>

              <Button
                onClick={generatePDF}
                variant="contained"
                size="small"
                disabled={selectedProperties.size === 0 || isGeneratingPDF}
                startIcon={isGeneratingPDF ? <CircularProgress size={16} color="inherit" /> : <GetApp sx={{ fontSize: 16 }} />}
                sx={{
                  backgroundColor: '#23afa4ff',
                  fontWeight: '500',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 1,
                  boxShadow: 2,
                  fontSize: '0.8rem',
                  minHeight: 32,
                  '&:hover': {
                    backgroundColor: '#1e8e8b',
                    boxShadow: 3,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isGeneratingPDF ? 'Generando...' : `Descargar PDF (${selectedProperties.size})`}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
      {/* Modal de cupones */}
      <CouponForm
        open={showCouponForm}
        onClose={() => setShowCouponForm(false)}
        selectedPropertyIds={Array.from(selectedProperties)}
        properties={filteredProperties}
        onSuccess={handleCouponSuccess}
      />
    </>
  );
};

export default CampaignPage;